import { useCallback, useMemo, useState } from "react"
import { Grid, MenuItem } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { MRT_ColumnDef } from "material-react-table"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    COMPANY_ADMIN_ORGANIZATION_WORKGROUPS_CREATE_ROUTE,
    COMPANY_ADMIN_ROUTE,
    getEditWorkgroupRoute,
} from "../../../../../../constants/routes"
import { DeleteConfirmDialog } from "../../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { useCurrentCompany } from "../../../../../company/hooks/useCurrentCompany"

import {
    CompanyAdminWorkgroupsQuery,
    WorkgroupsForCompanyResponse,
    deleteWorkgroupForCompany,
    fetchWorkgroupsForCompany,
} from "../workgroupsApi"
import { Workgroup } from "../workgroupsTypes"

const useStyles = makeStyles()((theme) => ({
    deleteItem: {
        color: theme.palette.tailwind.red[600],
    },
}))

const selectWorkgroups = (data: WorkgroupsForCompanyResponse) => data?.workgroups || []

export function OrganizationWorkgroups() {
    const { companyId } = useCurrentCompany()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const { classes } = useStyles()

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>({
        showConfirmModal: false,
        confirmModalText: "",
        itemId: null,
    })

    const WorkgroupsResult = useApiQuery(fetchWorkgroupsForCompany, {
        queryName: CompanyAdminWorkgroupsQuery.LIST,
        dependencies: {
            companyId,
        },
        select: selectWorkgroups,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(WorkgroupsResult)

    const columns = useMemo<MRT_ColumnDef<Workgroup>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("company-organization-workgroups-table-name-column"),
                accessorKey: "name",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("company-organization-workgroups-table-calendar-column"),
                accessorKey: "calendarEmailAddress",
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("company-organization-workgroups-table-members-column"),
                accessorKey: "memberCount",
            }),
        ],
        [l10n, columnFactory],
    )

    const { mutate: callDeleteWorkgroup } = useApiMutation(deleteWorkgroupForCompany, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [CompanyAdminWorkgroupsQuery.LIST] })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeleteWorkgroup({ workgroupId: confirmModalState.itemId })
        } else {
            throw new Error("Workgroup id is undefined")
        }
    }, [confirmModalState.itemId, callDeleteWorkgroup])

    return (
        <>
            <LocalizedStrict id="company-organization-workgroups-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_ADMIN_ORGANIZATION_WORKGROUPS}
                    title="Workgroups"
                    rightElement={
                        <LocalizedStrict id="company-organization-workgroups-create-button">
                            <StandardButton
                                onClick={() => navigate(COMPANY_ADMIN_ORGANIZATION_WORKGROUPS_CREATE_ROUTE)}
                            >
                                Create workgroup
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-title">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-workgroups-title">
                            <Breadcrumb>Workgroups</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <Grid container direction="column">
                    <ApiQueryTable
                        columns={columns}
                        initialState={{ density: "spacious" }}
                        renderRowActionMenuItems={({ row, closeMenu }) => [
                            <MenuItem
                                onClick={() => {
                                    navigate(getEditWorkgroupRoute(row.original.id))
                                    closeMenu()
                                }}
                                key={`edit-${row.original.id}`}
                            >
                                <LocalizedStrict id="company-organization-workgroups-table-edit">
                                    <>Edit</>
                                </LocalizedStrict>
                            </MenuItem>,
                            <MenuItem
                                className={classes.deleteItem}
                                onClick={() => {
                                    setConfirmModalState({
                                        showConfirmModal: true,
                                        confirmModalText: `${row.original.name}`,
                                        itemId: row.original.id,
                                    })
                                    closeMenu()
                                }}
                                key={`delete-${row.original.id}`}
                            >
                                <LocalizedStrict id="company-organization-workgroups-table-delete">
                                    <>Delete</>
                                </LocalizedStrict>
                            </MenuItem>,
                        ]}
                    />
                </Grid>
            </Grid>

            {confirmModalState.showConfirmModal && (
                <DeleteConfirmDialog
                    typeOfEntity={l10n.getString("company-organization-workgroups-table-delete-modal-title")}
                    entityName={confirmModalState.confirmModalText}
                    handleClick={handleDeleteClick}
                    open={confirmModalState.showConfirmModal}
                    onClose={() =>
                        setConfirmModalState({
                            showConfirmModal: false,
                            confirmModalText: "",
                            itemId: null,
                        })
                    }
                />
            )}
        </>
    )
}
